/**
 * A conversão entre o que se vê e o que se grava no recorte (E80.F1.C01).
 *
 * O servidor recorta o ORIGINAL e só então gira (é o que a ADR-0073 já fixou
 * para o recorte de assinatura, e o que os testes de pixel do servidor provam). A
 * caixa persistida, portanto, está em coordenadas do original — mas o perito
 * desenha sobre a imagem **girada**, que é a única em que ele consegue enquadrar
 * um rosto de lado.
 *
 * Sem converter, as duas caixas descrevem regiões diferentes assim que a foto
 * não é quadrada: era o F-E80.F1-01, em que o perito enquadrava o rosto e salvava
 * outro trecho da imagem.
 *
 * Convenção: coordenadas normalizadas em [0,1], origem no canto superior
 * esquerdo, e o giro é **horário** — o mesmo sentido do `rotate(Ndeg)` do CSS e
 * do `apply_rotation` do servidor.
 *
 * Como os quatro giros são múltiplos de 90°, retângulo alinhado aos eixos
 * continua alinhado depois da conversão: basta transformar dois cantos opostos
 * e reordenar.
 */
import type { CaixaDoRecorte, RotacaoDaFoto } from '@/lib/fotos_do_signatario';

interface Ponto {
  x: number;
  y: number;
}

/** Original → exibição: onde o ponto (x,y) do arquivo aparece depois do giro. */
function pontoParaExibicao({ x, y }: Ponto, rotacao: RotacaoDaFoto): Ponto {
  switch (rotacao) {
    case 90:
      return { x: 1 - y, y: x };
    case 180:
      return { x: 1 - x, y: 1 - y };
    case 270:
      return { x: y, y: 1 - x };
    default:
      return { x, y };
  }
}

/** Exibição → original: a inversa exata da de cima. */
function pontoParaOriginal({ x, y }: Ponto, rotacao: RotacaoDaFoto): Ponto {
  switch (rotacao) {
    case 90:
      return { x: y, y: 1 - x };
    case 180:
      return { x: 1 - x, y: 1 - y };
    case 270:
      return { x: 1 - y, y: x };
    default:
      return { x, y };
  }
}

function caixaEntrePontos(a: Ponto, b: Ponto): CaixaDoRecorte {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
}

function converter(
  caixa: CaixaDoRecorte,
  rotacao: RotacaoDaFoto,
  ponto: (p: Ponto, r: RotacaoDaFoto) => Ponto,
): CaixaDoRecorte {
  return caixaEntrePontos(
    ponto({ x: caixa.x, y: caixa.y }, rotacao),
    ponto({ x: caixa.x + caixa.w, y: caixa.y + caixa.h }, rotacao),
  );
}

/** A caixa desenhada na tela, traduzida para as coordenadas do arquivo. */
export function paraOriginal(caixa: CaixaDoRecorte, rotacao: RotacaoDaFoto): CaixaDoRecorte {
  return converter(caixa, rotacao, pontoParaOriginal);
}

/** A caixa gravada, traduzida para onde ela aparece na imagem girada. */
export function paraExibicao(caixa: CaixaDoRecorte, rotacao: RotacaoDaFoto): CaixaDoRecorte {
  return converter(caixa, rotacao, pontoParaExibicao);
}

/**
 * O tamanho em que a imagem é apresentada no editor, e o da própria imagem.
 *
 * O palco precisa ter as dimensões da imagem **girada** — é contra ele que o
 * arrasto é medido, e é sobre ele que o retângulo é posicionado em percentual.
 * A imagem, por baixo, mantém as dimensões dela e gira em torno do centro.
 */
export function medidasDoPalco(
  natural: { largura: number; altura: number },
  rotacao: RotacaoDaFoto,
  ladoMaximo: number,
): { palco: { largura: number; altura: number }; imagem: { largura: number; altura: number } } {
  const trocado = rotacao === 90 || rotacao === 270;
  const exibidaLargura = trocado ? natural.altura : natural.largura;
  const exibidaAltura = trocado ? natural.largura : natural.altura;
  const escala = Math.min(ladoMaximo / exibidaLargura, ladoMaximo / exibidaAltura);
  return {
    palco: { largura: exibidaLargura * escala, altura: exibidaAltura * escala },
    imagem: { largura: natural.largura * escala, altura: natural.altura * escala },
  };
}
